import React, { useState, useEffect, useMemo } from "react";
import { Table, InputGroup, Form, Row, Col, Badge, Button, ButtonGroup, Spinner } from "react-bootstrap";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import AnimationLoading from "../../components/Animationloading";
import { LINEAR_GRADIENT_BG, USER_KEY } from "../../const";
import { useLazyQuery } from "@apollo/client";
import { GET_CURRENCY_INVENTORIES, GET_LOT_INVENTORIES, GET_SURVIVAL_LOTS } from "../../apollo/inventory/query";
import EmptyComponent from "../../components/EmptyComponent";
import PaginationHelper from "../../components/PaginationHelper";
import { convertCurrency, covertTimeZone, numberFormat } from "../../helper";
import CurrentDateComponents from "../../components/CurrentDateComponents";
import { MdOutlineDateRange } from "react-icons/md";

export default function GroupCurrencyList(props) {
  const { _limit, _skip, Pagination_helper } = PaginationHelper();
  
  // GraphQL Queries
  const [getSurvivalLots, { data: lotsData, loading: loadingLots }] = 
    useLazyQuery(GET_SURVIVAL_LOTS, { fetchPolicy: "cache-and-network" });
  const [getCurrencyInventories, { data: dataCurrencyInventories, loading: loadingCurrencyInventories }] = 
    useLazyQuery(GET_CURRENCY_INVENTORIES, { fetchPolicy: "cache-and-network" });

  // State
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("createdAt_DESC");
  const [tagFilter, setTagFilter] = useState("ALL");
  const [selectedLot, setSelectedLot] = useState("ALL");
  
  // State select date
  const [isCurrentModals, setIsCurrentModals] = useState(false);
  const [activeButton, setActiveButton] = useState("");
  const [filter, setFilter] = useState({
    createdAt_gte: '',
    createdAt_lt: '',
  });

  // Sort options
  const sortOptions = [
    { value: "createdAt_DESC", label: "ວັນທີ່ລ່າສຸດ" },
    { value: "createdAt_ASC", label: "ວັນທີ່ເກົ່າສຸດ" }
  ];

  // Tag filter options
  const tagOptions = [
    { value: "ALL", label: "ເຂົ້າ-ອອກ" },
    { value: "IN", label: "ນຳເຂົ້າ" },
    { value: "OUT", label: "ນຳອອກ" }
  ];

  // Memoized data
  const currencyInventories = useMemo(() => {
    try {
      if (dataCurrencyInventories?.currencyInventories) {
        const parsedData = dataCurrencyInventories?.currencyInventories;
        
        if (parsedData?.status) {
          props.setIsTitle(`ຫລາຍສະກຸນເງິນໃນນື່ງລ໊ອດ(${parsedData?.total || 0})`);
          return parsedData || null;
        }
      }
      return null;
    } catch (error) {
      console.error("Error parsing currency inventory data:", error);
      return null;
    }
  }, [dataCurrencyInventories, props]);

  const lots = useMemo(() => {
    try {
      if (lotsData?.survivals?.data) {
        const data = lotsData.survivals.data || [];
        return data;
      }
      return [];
    } catch (error) {
      console.error("Error parsing lot data:", error);
      return [];
    }
  }, [lotsData]);

  const shop = useMemo(() => {
    const userData = localStorage.getItem(USER_KEY);
    const locals = JSON.parse(userData);
    return locals?.data?.shop
  }, [USER_KEY])

  // Effects
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    
    const fetch = async () => {
      try {
        await Promise.all([
          fetchCurrencyInventories(signal),
          fetchLots(signal)
        ]);
      } catch (error) {
        console.log("Error in fetch:", error);
      }
    }
    
    fetch();
    
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    
    if (signal.aborted) return;
    
    const timer = setTimeout(() => {
      fetchCurrencyInventories(signal);
    }, 100); // Debounce search
    
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchText, filter, sortBy, tagFilter, _skip, _limit, selectedLot]);

  // Fetch functions
  async function fetchLots(signal) {
    try {
      if (signal.aborted || loadingLots) return;
      if (!shop?.id) return;
      
      let where = {
        shop: shop.id,
        isDeleted: false
      };
      
      await getSurvivalLots({
        variables: {
          where
        },
        context: { fetchOptions: { signal } },
      });
    } catch (error) {
      console.error("Error fetching lots:", error);
    }
  }
  
  async function fetchCurrencyInventories(signal) {
    try {
      // if (signal.aborted || loadingLotInventories) return;
      if (!shop?.id) return;
      
      let where = {
        shops: [shop?.id],
        // All data default isDeleted: false
      };

      // Apply lot filter
      if (selectedLot && selectedLot !== "ALL") {
        where = { ...where, lot: selectedLot };
      }
      
      // Apply search filter
      if (searchText.trim() !== "") {
        where = { ...where, lotName: searchText.trim() };
      }
      
      // Apply tag filter
      if (tagFilter !== "ALL") {
        where = { ...where, tag: tagFilter };
      }

      // Apply createdAt filter
      if (filter?.createdAt_gte !== "" && filter?.createdAt_lt !== "") {
        where = {
          ...where,
          createdAt_gte: filter?.createdAt_gte,
          createdAt_lt: filter?.createdAt_lt
        };
      }
      
      await getCurrencyInventories({
        variables: {
          limit: _limit,
          skip: _skip - 1,
          orderBy: sortBy,
          where
        },
        context: { fetchOptions: { signal } },
      });
    } catch (error) {
      console.error("Error fetching currency inventories:", error);
    }
  }

  // Render helper functions
  const renderTagBadge = (tag) => {
    if (tag === "IN") {
      return <Badge bg="success">ນຳເຂົ້າ</Badge>;
    } else if (tag === "OUT") {
      return <Badge bg="danger">ນຳອອກ</Badge>;
    }
    return <Badge bg="secondary">-</Badge>;
  };
  
  // Render helper functions
  const renderTypeBadge = (type) => {
    if (type === "PURCHASE") {
      return <Badge bg="info">ໃບບິນສັ່ງຊື້ສິນຄ້າ</Badge>;
    } else if (type === "EXCEL") {
      return <Badge bg="success">Excel</Badge>;
    }
    return <Badge bg="secondary">{type || "-"}</Badge>;
  };

  return (
    <div className="group-lot-container">
      {/* Filters */}
      <div className="group-lot-filters" 
      // style={{ padding: "1em" }}
      >
        <Row className="mb-3">
          {/* Lot Name filter enter */}
          <Col md={3}>
            <InputGroup>
              <InputGroup.Text style={{ backgroundColor: "#FFFFFF" }}>
                <SearchIcon sx={{ color: "#6B7280" }} />
              </InputGroup.Text>
              <Form.Control
                placeholder="ຄົ້ນຫາລ໊ອດ..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ borderLeft: "none" }}
              />
            </InputGroup>
          </Col>
          {/* Lot filter select */}
          <Col md={2}>
            <Form.Select
              value={selectedLot}
              onChange={(e) => setSelectedLot(e.target.value)}
              style={{ color: "#6B7280" }}
            >
              <option value="ALL">ລ໊ອດທັງໝົດ</option>
              {loadingLots ? (
                <option disabled>ກຳລັງໂຫລດ...</option>
              ) : (
                lots.map((lot) => (
                  <option key={lot?.id} value={lot?.id}>
                    {lot?.survivalNumber}
                  </option>
                ))
              )}
            </Form.Select>
          </Col>
          {/* Tag filter select */}
          <Col md={2}>
            <Form.Select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              style={{ color: "#6B7280" }}
            >
              {tagOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Col>
          {/* SortBy filter select */}
          <Col md={2}>
            <Form.Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ color: "#6B7280" }}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Col>
          {/* Date filter buttons */}
          <Col md={3}>
            <Form.Group>
              <Button
                variant="outline-secondary"
                onClick={() => setIsCurrentModals(true)}
                style={{ textAlign: "left", marginTop: '-.2em', width: '100%' }}
              >
                {(loadingLots) ? (
                  <Spinner size="sm" />
                ) : (
                  <MdOutlineDateRange style={{ fontSize: 24 }} />
                )}
                &nbsp;  &nbsp;
                {(filter?.createdAt_gte === "" && filter?.createdAt_lt === "") ? (
                  <span style={{ fontSize: 14 }}>ຄົ້ນຫາຕາມວັນທີ່...</span>
                ) : (
                  <span style={{ fontSize: 14 }}>
                    {covertTimeZone(filter?.createdAt_gte) +
                      " - " +
                      covertTimeZone(filter?.createdAt_lt)}
                  </span>
                )}
              </Button>
            </Form.Group>
          </Col>
        </Row>
      </div>
      
      {/* Table */}
      <div className="group-lot-table" style={{ padding: "0 1em" }}>
        {currencyInventories?.total === 0 || loadingCurrencyInventories ? (
          <>
          {
            loadingCurrencyInventories ? (<AnimationLoading />) : <EmptyComponent title="ບໍ່ພົບຂໍ້ມູນເງິນ" />
          }
          </>
        ) : (
          <Table 
            responsive
            style={{
              marginTop: "10px",
              borderCollapse: "collapse",
              width: "100%",
              background: "#fff"
            }}
          >
            <thead
              style={{
                background: LINEAR_GRADIENT_BG,
                borderRadius: "8px 8px 0px 0px",
                color: "#FFFFFF",
              }}
            >
              <tr>
                <th style={{ width: "5%" }}>ລຳດັບ</th>
                <th style={{ width: "15%" }}>ລ໊ອດ</th>
                <th style={{ width: "15%" }}>ວັນທີ່</th>
                <th style={{ width: "10%" }}>ຈ/ນລາຍການ</th>
                <th style={{ width: "10%" }}>ຈ/ນທັງໝົດ</th>
                <th style={{ width: "15%" }}>ລາຄາລວມ</th>
                <th style={{ width: "15%" }}>ແທັກ</th>
                <th style={{ width: "10%" }}>ຜ່ານ</th>
                <th style={{ width: "10%" }}>ໂດຍ</th>
              </tr>
            </thead>
            <tbody>
              {currencyInventories?.dataView?.map((lotIn, index) => (
                <tr key={lotIn?._id}>
                  <td>{(_skip - 1) + index + 1}</td>
                  <td>{lotIn?.lotName}</td>
                  <td>{moment(lotIn?.createdAt).format("DD/MM/YYYY HH:mm")}</td>
                  <td>{numberFormat(lotIn?.documentCount || 0)}</td>
                  <td>{numberFormat(lotIn?.totalAmount || 0)}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <span>{convertCurrency(lotIn?.currency || "LAK")}</span>
                      <span>{numberFormat(lotIn?.totalPrice || 0)}</span>
                    </div>
                  </td>
                  <td>{renderTagBadge(lotIn?.tag)}</td>
                  <td>{renderTypeBadge(lotIn?.type) || "-"}</td>
                  <td>{lotIn?.createdByName || "-"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        
        {/* Pagination */}
        <div className="d-flex justify-content-center mt-3 mb-5">
          {Pagination_helper(currencyInventories?.total || 0)}
        </div>
      </div>
      {/* //TODO Modal select date lenght */}
      <CurrentDateComponents
        onCloseModal={() => setIsCurrentModals(false)}
        openModal={isCurrentModals}
        setCdFillter={setFilter}
        setActiveButton={setActiveButton}
        activeButton={activeButton}
      />
    </div>
  );
}
